import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Mail, Phone, Globe, Accessibility, ChevronDown, Coffee } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { LOGO_URL } from "@/constants/site";

export default function Footer() {
  const year = new Date().getFullYear();
  const [a11yOpen, setA11yOpen] = useState(false);
  return (
    <footer className="relative bg-[var(--surface-dark)] text-white mt-12 rounded-t-[32px] overflow-hidden">
      <div className="absolute inset-0 grid-pattern-dark pointer-events-none opacity-60" />
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[70%] h-[300px] cyan-radial pointer-events-none" />

      <div className="relative max-w-[1600px] mx-auto px-6 md:px-10 py-12 md:py-14">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
          <div className="md:col-span-5">
            <span className="chip !bg-white/5 !text-cyan-300 !border-cyan-400/20">
              <span className="chip-dot" /> Ready when you are
            </span>
            <h3 className="mt-4 font-editorial text-2xl md:text-3xl lg:text-4xl leading-[1.05] tracking-tight text-white">
              Let&rsquo;s design an event solution{" "}
              <span className="bg-gradient-to-r from-cyan-300 to-sky-400 bg-clip-text text-transparent">
                worth remembering.
              </span>
            </h3>
            <div className="mt-5 flex flex-wrap items-center gap-3">
              <Link to="/contact" className="btn-primary" data-testid="footer-cta">
                Start a Project <ArrowRight size={16} />
              </Link>

              {/* Coffee-or-tea motif — gold + cyan, tiny + creative */}
              <button
                type="button"
                onClick={() => window.dispatchEvent(new Event("open-coffee-popup"))}
                data-testid="footer-coffee-cta"
                className="group relative inline-flex items-center gap-3 rounded-full pl-2 pr-4 py-2 bg-white/[0.04] border border-white/10 hover:border-amber-300/50 transition-all duration-300"
                aria-label="Open our coffee gift-card offer"
              >
                {/* Twin badge — amber cup + cyan cup, overlapping */}
                <span className="relative flex items-center">
                  <span className="relative z-[2] w-8 h-8 rounded-full bg-gradient-to-br from-amber-300 to-amber-500 text-[#1A1204] flex items-center justify-center shadow-[0_6px_16px_-6px_rgba(214,167,72,0.6)] group-hover:-rotate-6 transition-transform">
                    <Coffee size={14} strokeWidth={2.4} />
                    {/* steam wisp */}
                    <span
                      aria-hidden="true"
                      className="absolute -top-2 left-1/2 -translate-x-1/2 w-[3px] h-3 bg-white/70 rounded-full blur-[2px]"
                      style={{ animation: "coffeeSteamA 3s ease-in-out infinite" }}
                    />
                  </span>
                  <span className="relative -ml-3 w-8 h-8 rounded-full bg-gradient-to-br from-cyan-300 to-sky-500 text-[#052030] flex items-center justify-center shadow-[0_6px_16px_-6px_rgba(6,182,212,0.6)] group-hover:rotate-6 transition-transform">
                    <Coffee size={14} strokeWidth={2.4} />
                  </span>
                </span>

                {/* Text */}
                <span className="flex flex-col leading-tight">
                  <span className="font-mono-brand text-[9px] uppercase tracking-[0.24em] text-amber-300/85">
                    or...
                  </span>
                  <span className="text-white text-[13.5px] font-medium">
                    Coffee&rsquo;s on us{" "}
                    <ArrowRight
                      size={11}
                      className="inline align-baseline group-hover:translate-x-0.5 transition-transform"
                    />
                  </span>
                </span>
              </button>
            </div>
          </div>

          <div className="md:col-span-2">
            <p className="font-mono-brand text-[11px] uppercase tracking-[0.22em] text-white/50 mb-3">
              Agency
            </p>
            <ul className="space-y-1.5 text-white/80 text-sm">
              <li><Link to="/" className="link-sweep" data-testid="footer-link-home">Home</Link></li>
              <li><Link to="/services#chapter-01" className="link-sweep" data-testid="footer-link-strategic">Strategic Meeting Management</Link></li>
              <li><Link to="/services/event-tech" className="link-sweep" data-testid="footer-link-event-tech">Event Tech</Link></li>
              <li><Link to="/services/me-dmc" className="link-sweep" data-testid="footer-link-me-dmc">M.E. DMC</Link></li>
              <li><Link to="/services/vip-film" className="link-sweep" data-testid="footer-link-vip-film">VIP &amp; Film</Link></li>
              <li><Link to="/services/site-selection" className="link-sweep" data-testid="footer-link-site-selection">Site Selection</Link></li>
              <li><Link to="/b2b-services" className="link-sweep" data-testid="footer-link-b2b">B2B Services</Link></li>
              <li><Link to="/contact" className="link-sweep" data-testid="footer-link-contact">Contact</Link></li>
            </ul>
          </div>

          <div className="md:col-span-2">
            <p className="font-mono-brand text-[11px] uppercase tracking-[0.22em] text-white/50 mb-3">
              Direct
            </p>
            <ul className="space-y-1.5 text-white/80 text-sm">
              <li className="flex items-center gap-3">
                <Phone size={14} className="text-cyan-400" />
                <a href="tel:+18882992250" className="link-sweep">
                  888-299-2250
                </a>
              </li>
              <li className="flex items-center gap-3 text-white/50">
                <Globe size={14} className="text-cyan-400" />
                Global operations · HQ North America
              </li>
            </ul>
          </div>

          {/* ── Legal (right side) ── */}
          <div className="md:col-span-3">
            <p className="font-mono-brand text-[11px] uppercase tracking-[0.22em] text-white/50 mb-5">
              Legal
            </p>
            <ul className="space-y-3 text-white/80 text-sm">
              <li>
                <Link to="/privacy" className="link-sweep" data-testid="footer-link-privacy">
                  Privacy Policy
                </Link>
              </li>
              <li>
                <Link to="/terms" className="link-sweep" data-testid="footer-link-terms">
                  Terms &amp; Conditions
                </Link>
              </li>
              <li>
                <button
                  type="button"
                  onClick={() => setA11yOpen((v) => !v)}
                  aria-expanded={a11yOpen}
                  aria-controls="footer-a11y-panel"
                  data-testid="footer-link-accessibility"
                  className="link-sweep inline-flex items-center gap-2 text-left"
                >
                  <Accessibility size={13} className="text-cyan-400" />
                  Accessibility Statement
                  <ChevronDown
                    size={13}
                    className={`text-cyan-300/70 transition-transform duration-300 ${a11yOpen ? "rotate-180" : ""}`}
                  />
                </button>
              </li>
            </ul>

            {/* Accessibility disclaimer — hidden until user clicks the toggle above */}
            <AnimatePresence initial={false}>
              {a11yOpen && (
                <motion.div
                  key="footer-a11y-panel"
                  id="footer-a11y-panel"
                  initial={{ opacity: 0, height: 0, marginTop: 0 }}
                  animate={{ opacity: 1, height: "auto", marginTop: 24 }}
                  exit={{ opacity: 0, height: 0, marginTop: 0 }}
                  transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
                  data-testid="footer-a11y-disclaimer"
                  className="overflow-hidden"
                >
                  <div className="rounded-2xl border border-cyan-400/20 bg-white/[0.03] px-4 py-4">
                    <div className="flex items-start gap-3">
                      <Accessibility size={16} className="mt-0.5 text-cyan-300 shrink-0" />
                      <div>
                        <p className="text-[12.5px] leading-[1.55] text-white/75">
                          <span className="text-white font-medium">Need an accommodation?</span>{" "}
                          We&rsquo;re committed to equal access. If you use assistive
                          technology, need materials in an alternative format, or hit
                          a barrier on this site, please reach out to{" "}
                          <a
                            href="mailto:dion@mageeenterprises.com?subject=Accessibility%20accommodation%20request"
                            className="text-cyan-300 underline underline-offset-4 hover:no-underline"
                            data-testid="footer-a11y-email"
                          >
                            dion@mageeenterprises.com
                          </a>. We reply within 2 business days.
                        </p>
                        <Link
                          to="/accessibility"
                          className="mt-3 inline-flex items-center gap-1.5 font-mono-brand text-[10px] uppercase tracking-[0.22em] text-cyan-300 hover:text-white transition-colors"
                          data-testid="footer-a11y-full-link"
                        >
                          Read full statement →
                        </Link>
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>

        <div className="mt-8 pt-5 border-t border-white/10 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="Magee Enterprises" className="h-12 bg-white rounded-lg p-1" />
            <span className="font-mono-brand text-[10px] uppercase tracking-[0.24em] text-white/50">
              Plan · Lead · Inspire
            </span>
          </div>
          <div className="flex flex-col md:flex-row items-start md:items-center gap-2 md:gap-6">
            <div className="flex items-center gap-4 flex-wrap">
              <Link to="/privacy" className="font-mono-brand text-[10px] uppercase tracking-[0.24em] text-white/40 hover:text-white/80 transition-colors">
                Privacy
              </Link>
              <Link to="/terms" className="font-mono-brand text-[10px] uppercase tracking-[0.24em] text-white/40 hover:text-white/80 transition-colors">
                Terms
              </Link>
              <Link to="/accessibility" className="font-mono-brand text-[10px] uppercase tracking-[0.24em] text-white/40 hover:text-white/80 transition-colors">
                Accessibility
              </Link>
            </div>
            <span className="font-mono-brand text-[10px] uppercase tracking-[0.24em] text-white/40">
              © 2026&ndash;2027 Magee Enterprises. All rights reserved.
            </span>
          </div>
        </div>
      </div>
    </footer>
  );
}
